import axios from 'axios';
import qs from 'qs';
import { createInfiniteQuery, createMutation, createQuery } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { filterEmptyParams, restUrl } from '@/services/utils';
import { ServerErrorResponse } from '@/types/ServerErrorResponse';

import { BulkSubscription, BulkSubscriptionEntry } from './bulk-subscriptions.types';

const END_POINT = 'bulkSubscription';

// Dedicated axios instance for the cross-origin PUT to a Google Cloud
// Storage signed URL. Crucially it does NOT inherit the app-wide request
// interceptors installed on the default `axios` instance (e.g. the
// `Accept-Language` header set in `components/datamodel/queries.ts`) — GCS
// only expects the CORS-safelisted headers plus `Content-Type`, and any
// extra header would either be echoed into `Access-Control-Request-Headers`
// on the preflight (which the bucket rejects) or ignored on the wire.
// Using a bare instance is defence-in-depth on top of the language-tag
// normalisation in queries.ts.
const gcsUpload = axios.create();

interface CreateBulkSubscriptionPayload {
    label: string;
    name: string;
    description: string;
    attributes: string[];
    active: boolean;
}

interface BulkSubscriptionQueryResponse {
    data: BulkSubscription[];
    cursor?: string;
}

interface BulkSubscriptionQueryVariables {
    limit: number;
    keywords?: string;
    active?: boolean;
}

interface BulkSubscriptionEntriesQueryVariables {
    bulkSubscriptionId: number;
    offset: number;
    limit: number;
    keywords?: string;
}

interface BulkSubscriptionEntriesCountVariables {
    bulkSubscriptionId: number;
    keywords?: string;
}

/**
 * Server-side dispatch of the uploaded file. Mirrors the `action` query
 * parameter of `POST /bulkSubscription/{id}/_upload` in
 * `BulkSubscriptionResource.java`:
 *
 * - `ADD_ENTRIES` (default): append the uploaded items to the existing
 *   entries.
 * - `REPLACE_ENTRIES`: wipe every existing entry first, then add the
 *   uploaded items.
 * - `DELETE_ENTRIES`: remove entries that appear in the uploaded file;
 *   keep the rest.
 */
type BulkSubscriptionImportAction = 'ADD_ENTRIES' | 'REPLACE_ENTRIES' | 'DELETE_ENTRIES';

interface StageBulkSubscriptionFileVariables {
    file: File;
    /**
     * Called with the current upload percentage in `[0, 100]` as the file
     * bytes are being streamed to GCS. Wired to axios' `onUploadProgress`
     * so the Uploader can render a live progress bar and remaining-time
     * estimate. Optional: consumers that don't care about progress simply
     * omit it and the mutation behaves like a fire-and-forget PUT.
     */
    onProgress?: (percent: number) => void;
    /**
     * Optional abort signal. When aborted mid-flight, both the
     * `/gatherings/uploadurl` POST and the follow-up GCS PUT stop; any
     * partial GCS object is cleaned up by the storage lifecycle rules
     * that already govern orphaned upload URLs.
     */
    signal?: AbortSignal;
}

interface StagedBulkSubscriptionFile {
    /**
     * Opaque asset key returned by `POST /gatherings/uploadurl`. Pass it
     * back into {@link useImportStagedBulkSubscriptionFile} to finalize
     * the digital asset and enqueue the import.
     */
    assetKey: string;
}

interface ImportStagedFileVariables {
    entityId: number;
    /**
     * Asset key produced by {@link stageBulkSubscriptionFile}. The server
     * finalizes the GCS-hosted upload identified by this key and queues a
     * `BulkSubscriptionImport` for the given action.
     */
    assetKey: string;
    /**
     * Which server-side action to run on the imported file. Defaults to
     * `ADD_ENTRIES` on the server if unset, matching the Java resource's
     * `DEFAULT_BULK_SUBSCRIPTION_IMPORT_ACTION`.
     */
    action?: BulkSubscriptionImportAction;
}

const useCreateBulkSubscription =
    createMutation<BulkSubscription, CreateBulkSubscriptionPayload, ServerErrorResponse>({
        mutationFn: async (bulkSubscription) => {
            const response = await axios.post(restUrl(END_POINT), { ...bulkSubscription });

            return response.data;
        }
    });

const useDeleteBulkSubscription =
    createMutation<Record<string, never>, number, ServerErrorResponse>({
        mutationFn: async (bulkSubscriptionId) => {
            const response = await axios.delete(`${restUrl(END_POINT)}/${bulkSubscriptionId}`, {
                data: [],
            });

            return response.data;
        }
    });

interface DeleteAllEntriesVariables {
    bulkSubscriptionId: number;
    // Restricts the server-side delete to entries matching the keyword search;
    // omit (or pass an empty string) to wipe every entry.
    keywords?: string;
}

const useDeleteBulkSubscriptionEntries =
    createMutation<Record<string, never>, DeleteAllEntriesVariables, ServerErrorResponse>({
        mutationFn: async ({ bulkSubscriptionId, keywords }) => {
            const response = await axios.delete(
                `${restUrl(END_POINT)}/${bulkSubscriptionId}/entries/all`,
                {
                    params: { keywords },
                    paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
                },
            );

            return response.data;
        }
    });

interface DeleteSelectedEntriesVariables {
    bulkSubscriptionId: number;
    entries: Record<string, unknown>[];
}

const useDeleteSelectedBulkSubscriptionEntries =
    createMutation<Record<string, never>, DeleteSelectedEntriesVariables, ServerErrorResponse>({
        mutationFn: async ({ bulkSubscriptionId, entries }) => {
            const response = await axios.delete(
                `${restUrl(END_POINT)}/${bulkSubscriptionId}/entries`,
                { data: entries },
            );

            return response.data;
        }
    });

const useBulkSubscriptions =
    createInfiniteQuery<BulkSubscriptionQueryResponse, BulkSubscriptionQueryVariables, Error, string | undefined>({
        queryKey: [END_POINT],
        fetcher: async (variables, { pageParam }) => {

            const response = await axios.get(restUrl(END_POINT), {
                params: variables,
                headers: {
                    [HTTP_HEADERS.X_CURSOR]: pageParam
                },
                paramsSerializer: (params) => {
                    return qs.stringify(filterEmptyParams(params));
                },
            });

            return {
                data: response.data?? [],
                cursor: response.headers[HTTP_HEADERS.X_CURSOR] ?? undefined
            };
        },
        getNextPageParam: (lastPageRequestResponse) => lastPageRequestResponse.cursor,
        initialPageParam: undefined
    });

const useBulkSubscriptionEntries =
    createQuery<BulkSubscriptionEntry[], BulkSubscriptionEntriesQueryVariables, Error>({
        queryKey: [END_POINT, 'entries'],
        fetcher: async ({ bulkSubscriptionId, offset, limit, keywords }) => {

            const response = await axios.get<BulkSubscriptionEntry[]>(
                `${restUrl(END_POINT)}/${bulkSubscriptionId}/entries`,
                {
                    params: { offset, limit, keywords },
                    paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
                }
            );

            return response.data ?? [];
        }
    });

const useBulkSubscriptionEntriesCount =
    createQuery<number, BulkSubscriptionEntriesCountVariables, Error>({
        queryKey: [END_POINT, 'entries', 'count'],
        fetcher: async ({ bulkSubscriptionId, keywords }) => {
            const response = await axios.get<number>(
                `${restUrl(END_POINT)}/${bulkSubscriptionId}/entries/count`,
                {
                    params: { keywords },
                    paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
                }
            );

            return Number(response.data) || 0;
        }
    });

/**
 * Uploads the raw file bytes to Google Cloud Storage using the same
 * signed-URL flow as the asset-folder large-file uploader
 * (`useAssetFolderUploader`). Splits the upload into three real network
 * calls so the UI can present a proper "upload → import" flow instead of
 * cramming both steps into a single opaque request:
 *
 * 1. `POST /gatherings/uploadurl` – asks the server for a fresh gathering
 *    and a signed GCS URL the browser is allowed to write to.
 * 2. `PUT <uploadUrl>` – streams the file bytes directly to GCS, emitting
 *    upload progress via {@link StageBulkSubscriptionFileVariables.onProgress}.
 * 3. The returned {@link StagedBulkSubscriptionFile.assetKey} is later
 *    fed back into
 *    {@link useImportStagedBulkSubscriptionFile} which triggers
 *    `POST /bulkSubscription/{id}/_upload?assetKey=…`, letting the server
 *    finalize the digital asset (mime-type check, tagging, upload-type)
 *    and enqueue a `BulkSubscriptionImport`.
 *
 * A staged upload that never gets imported (e.g. because the user closes
 * the dialog) simply leaves an orphaned GCS object which the storage
 * lifecycle already cleans up — matching the existing large-file flow.
 */
async function stageBulkSubscriptionFile({
    file,
    onProgress,
    signal,
}: StageBulkSubscriptionFileVariables): Promise<StagedBulkSubscriptionFile> {
    const contentType = file.type || 'application/octet-stream';
    const uploadUrlEndpoint =
        `${restUrl('gatherings/uploadurl')}` +
        `?filename=${encodeURIComponent(file.name)}` +
        `&contentType=${encodeURIComponent(contentType)}`;

    const urlResponse = await axios.post<{ uploadUrl: string; assetKey: string }>(
        uploadUrlEndpoint,
        null,
        { signal },
    );
    const { uploadUrl, assetKey } = urlResponse.data ?? {};
    if (!uploadUrl || !assetKey) {
        throw new Error('Missing uploadUrl or assetKey in /gatherings/uploadurl response');
    }

    await gcsUpload.put(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
        signal,
        onUploadProgress: (event) => {
            if (!onProgress) {
                return;
            }
            const total = event.total ?? file.size;
            if (!total) {
                return;
            }
            const percent = Math.max(
                0,
                Math.min(100, Math.round((event.loaded / total) * 100)),
            );
            onProgress(percent);
        },
    });

    return { assetKey };
}

const useStageBulkSubscriptionFile =
    createMutation<StagedBulkSubscriptionFile, StageBulkSubscriptionFileVariables, ServerErrorResponse>({
        mutationFn: stageBulkSubscriptionFile,
    });

/**
 * Finalizes the GCS-hosted upload identified by `assetKey` and enqueues a
 * `BulkSubscriptionImport` for the given `action`. Should only be called
 * after a successful {@link useStageBulkSubscriptionFile}, which is what
 * produces the `assetKey`.
 *
 * The server-side counterpart is
 * `BulkSubscriptionResource.uploadBulkSubscriptionEntriesForId`; when
 * `assetKey` is present it routes into `completeBulkSubscriptionUpload`
 * rather than the multipart path.
 */
const useImportStagedBulkSubscriptionFile =
    createMutation<Record<string, never>, ImportStagedFileVariables, ServerErrorResponse>({
        mutationFn: async ({ entityId, assetKey, action }) => {
            const response = await axios.post(
                `${restUrl(END_POINT)}/${entityId}/_upload`,
                null,
                {
                    params: { assetKey, action },
                    paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
                },
            );

            return response.data;
        }
    });

const useUpdateBulkSubscription =
    createMutation<Record<string, never>, BulkSubscription, ServerErrorResponse>({
        mutationFn: async (entity) => {
            const { id, ...entityToSend } = entity;
            const response = await axios.put(`${restUrl(END_POINT)}/${entity.id}`, entityToSend);

            return response.data;
        }
    });

export type BulkSubscriptionExportFormat = 'EXCEL' | 'JSON';

interface ExportEntriesVariables {
    bulkSubscriptionId: number;
    format: BulkSubscriptionExportFormat;
}

const FALLBACK_EXTENSION: Record<BulkSubscriptionExportFormat, string> = {
    EXCEL: 'xlsx',
    JSON: 'json',
};

const extractFileName = (contentDisposition: string | undefined, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    return match ? decodeURIComponent(match[1]) : fallback;
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
};

const useExportBulkSubscriptionEntries =
    createMutation<void, ExportEntriesVariables, ServerErrorResponse>({
        mutationFn: async ({ bulkSubscriptionId, format }) => {
            const response = await axios.get(
                `${restUrl(END_POINT)}/${bulkSubscriptionId}/entries/_export`,
                {
                    params: { format },
                    responseType: 'blob',
                }
            );

            const fileName = extractFileName(
                response.headers['content-disposition'],
                `bulk-subscription-${bulkSubscriptionId}.${FALLBACK_EXTENSION[format]}`
            );

            triggerBrowserDownload(response.data, fileName);
        }
    });

export type { BulkSubscriptionImportAction, StagedBulkSubscriptionFile };

export {
    useBulkSubscriptionEntries,
    useBulkSubscriptionEntriesCount,
    useBulkSubscriptions,
    useCreateBulkSubscription,
    useDeleteBulkSubscription,
    useDeleteBulkSubscriptionEntries,
    useDeleteSelectedBulkSubscriptionEntries,
    useExportBulkSubscriptionEntries,
    useImportStagedBulkSubscriptionFile,
    useStageBulkSubscriptionFile,
    useUpdateBulkSubscription,
};
